/**
 * Denial Intake upload-module API wrappers for the DM SPA.
 *
 * The customer uploads raw files; the backend extracts text, builds the CM
 * JSON via the CM extraction process, and persists a DenialCase the user can
 * review/edit (private by default) and optionally push to the global queue.
 */
import { dmClient } from '../../services/dmClient';

// Uploads + CM extraction can take a while -- give them a generous timeout.
const UPLOAD_TIMEOUT = 600000;

export const intakeApi = {
  upload: async (files, { name, visibility } = {}) => {
    const formData = new FormData();
    (files || []).forEach((f) => formData.append('files', f));
    if (name) formData.append('name', name);
    if (visibility) formData.append('visibility', visibility);
    const resp = await dmClient.post('/intake/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: UPLOAD_TIMEOUT,
    });
    return resp.data;
  },

  listCases: async ({ queue = 'mine', page = 1, pageSize = 50 } = {}) => {
    const resp = await dmClient.get('/intake/cases', {
      params: { queue, page, pageSize },
    });
    return resp.data;
  },

  getCase: async (caseId) => {
    const resp = await dmClient.get(`/intake/cases/${caseId}`);
    return resp.data;
  },

  updateCase: async (caseId, { cmJson, missingItems } = {}) => {
    const resp = await dmClient.patch(`/intake/cases/${caseId}`, {
      cmJson,
      missingItems,
    });
    return resp.data;
  },

  addDocuments: async (caseId, files) => {
    const formData = new FormData();
    (files || []).forEach((f) => formData.append('files', f));
    const resp = await dmClient.post(`/intake/cases/${caseId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: UPLOAD_TIMEOUT,
    });
    return resp.data;
  },

  setVisibility: async (caseId, visibility) => {
    const resp = await dmClient.post(`/intake/cases/${caseId}/visibility`, { visibility });
    return resp.data;
  },

  missingItems: async (caseId) => {
    const resp = await dmClient.get(`/intake/cases/${caseId}/missing-items`);
    return resp.data;
  },

  submit: async (caseId, { engine } = {}) => {
    const resp = await dmClient.post(`/intake/cases/${caseId}/submit`, { engine: engine || undefined });
    return resp.data;
  },

  // Direct upsert of a pre-built CM denial_payload JSON (no extraction needed).
  // Mirrors what tools/denial_v4/scripts/eval_one_easy_test.py does so users can
  // drop a seed file (e.g. seed-coding-ncci-easy-hard-bundle-carc97.json) onto
  // /DenialResolve and run it through the v4 engine.
  submitDenialPayload: async (payload, meta = {}) => {
    const resp = await dmClient.post('/denials', {
      externalId: meta.externalId || payload?.case_id,
      archetype: meta.archetype || payload?.archetype,
      complexity: meta.complexity || payload?.complexity_level,
      payload,
    });
    return resp.data;
  },
};

export default intakeApi;
